import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import DataTable, { Column } from '../../components/DataTable';
import ExportModal from '../../components/ExportModal';
import FilterBar, { ForeignKeyOption } from '../../components/FilterBar';
import DuplicateCleanupPanel from '../../components/DuplicateCleanupPanel';
import { entityFieldsToExportFields } from '../../utils/exportUtils';
import { memberFields, FilterValue, applyFilters } from '../../utils/entityFields';
import { ConfirmModal, SkeletonTable, Button } from '../../components/ui';
import EmptyState from '../../components/ui/EmptyState';
import Modal from '../../components/Modal';
import SearchableSelect from '../../components/SearchableSelect';
import SchedulePromptModal from '../../components/SchedulePromptModal';
import type { GroupSchedulePreview, ScheduleAction, ScheduleSelection } from '../../types/schedulePrompt';
import PageHeader from '../../components/PageHeader';
import './MembersListPage.css';
import { translateError } from '../../utils/errorMessages';

// =============================================================================
// TYPES
// =============================================================================

interface IGroupDisplay {
  _id: string;
  name: string;
  /** Virtual course/activity group: membership follows enrollment, not editable here. */
  managed?: { kind: 'course' | 'activity' } | null;
}

/** D6 prompt for a bulk group add / remove, run through the group endpoints. */
interface BulkGroupPrompt {
  groupId: string;
  action: ScheduleAction;
  memberIds: number[];
  preview: GroupSchedulePreview | null;
  error: string | null;
}

interface IUserDisplay {
  _id: string;
  name: string;
  email: string;
  admin?: boolean;
}

interface IMember {
  _id: string;
  name: string;
  description: string;
  color?: string;
  createdAt?: string;
  user?: IUserDisplay;
  groups?: IGroupDisplay[];
  email?: string;
  phone?: string;
  /**
   * Count of activity enrollments for the member (from the backend list
   * response). A value of 0 means the member is "unattached" — the new term
   * that replaces the legacy `type === 'external'` binary.
   */
  activityCount?: number;
}

type MemberFilter = 'all' | 'unattached';

// =============================================================================
// COMPONENT
// =============================================================================

const MembersListPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [members, setMembers] = useState<IMember[]>([]);
  const [users, setUsers] = useState<IUserDisplay[]>([]);
  const [groups, setGroups] = useState<IGroupDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [filters, setFilters] = useState<FilterValue[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [anonymizeId, setAnonymizeId] = useState<string | null>(null);
  const [anonymizeLoading, setAnonymizeLoading] = useState(false);
  const [memberFilter, setMemberFilter] = useState<MemberFilter>('all');
  const [showCleanupPanel, setShowCleanupPanel] = useState(false);
  const [attendanceBadges, setAttendanceBadges] = useState<Map<string, { rate: number; total: number }>>(new Map());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<'addGroup' | 'removeGroup' | null>(null);
  const [bulkGroupId, setBulkGroupId] = useState('');
  const [bulkPrompt, setBulkPrompt] = useState<BulkGroupPrompt | null>(null);
  const [bulkPromptConfirming, setBulkPromptConfirming] = useState(false);

  // Permission checks
  const canCreate = hasPermission('members:write');
  const canEdit = hasPermission('members:write');
  const canDelete = hasPermission('members:delete');
  const canAnonymize = hasPermission('members:anonymize');
  const canViewUsers = hasPermission('users:read');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch all members and groups in parallel
      const [membersRes, groupsRes] = await Promise.all([
        api.members.list(),
        api.groups.list(),
      ]);

      setMembers(membersRes.data as IMember[]);
      setGroups(groupsRes.data as IGroupDisplay[]);

      // Fetch attendance badges (non-critical, allowed to fail)
      try {
        const badgesRes = await api.statistics.getAttendanceBadges();
        const badgeList = ((badgesRes.data as Record<string, unknown>)?.badges ?? []) as
          { memberId: string; attendanceRate: number; totalEvents: number }[];
        const badgeMap = new Map<string, { rate: number; total: number }>();
        for (const b of badgeList) {
          badgeMap.set(b.memberId, { rate: b.attendanceRate, total: b.totalEvents });
        }
        setAttendanceBadges(badgeMap);
      } catch {
        // Badges are non-critical - silently ignore
      }

      // Fetch users if user has permission
      if (canViewUsers) {
        try {
          const usersRes = await api.users.list();
          setUsers(usersRes.data as IUserDisplay[]);
        } catch {
          // Ignore error - user might not have permission
        }
      }
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, [canViewUsers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteClick = (member: IMember) => {
    if (!canDelete) return;
    setDeleteId(member._id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      const { data } = await api.members.delete(deleteId);
      fetchData();
      // The delete is a soft delete: say what was kept, so "their payment
      // history was preserved" actually reaches the admin.
      const retained = data?.retained;
      const kept = (retained?.payments ?? 0) + (retained?.attendanceRecords ?? 0);
      if (data?.alreadyDeleted) {
        addToast(t('dataSafety.memberAlreadyDeleted'), 'info');
      } else if (kept > 0) {
        addToast(
          t('dataSafety.memberDeletedRetained', {
            payments: retained?.payments ?? 0,
            attendance: retained?.attendanceRecords ?? 0,
          }),
          'success',
        );
      } else {
        addToast(t('members.deleteSuccess'), 'success');
      }
    } catch (err: any) {
      addToast(translateError(err, t, 'members.deleteError'), 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  const handleAnonymizeClick = (member: IMember) => {
    if (!canAnonymize) return;
    setAnonymizeId(member._id);
  };

  const handleAnonymizeConfirm = async () => {
    if (!anonymizeId) return;
    setAnonymizeLoading(true);
    try {
      await api.members.anonymize(anonymizeId);
      fetchData();
      addToast(t('members.anonymizeSuccess'), 'success');
    } catch (err: any) {
      addToast(translateError(err, t, 'members.anonymizeError'), 'error');
    } finally {
      setAnonymizeLoading(false);
      setAnonymizeId(null);
    }
  };

  // Clear selection when member filter tab or filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [memberFilter, filters]);

  const finishBulk = useCallback((action: ScheduleAction) => {
    addToast(t(action === 'add' ? 'bulk.addToGroupSuccess' : 'bulk.removeFromGroupSuccess'), 'success');
    setSelectedIds(new Set());
    setBulkPrompt(null);
    fetchData();
  }, [addToast, t, fetchData]);

  const commitBulk = useCallback(async (prompt: BulkGroupPrompt, selection: ScheduleSelection | null) => {
    // No selection = only the group membership changes.
    const payload = {
      memberIds: prompt.memberIds,
      ...(selection ? { templateIds: selection.templateIds, eventIds: selection.eventIds } : {}),
    };
    if (prompt.action === 'add') await api.groups.addMembers(prompt.groupId, payload);
    else await api.groups.removeMembers(prompt.groupId, payload);
  }, []);

  // Plain groups only (managed ones follow enrollment); same D6 prompt as the group page.
  const handleBulkAction = useCallback(async () => {
    if (!bulkGroupId || selectedIds.size === 0 || !bulkAction) return;
    const action: ScheduleAction = bulkAction === 'addGroup' ? 'add' : 'remove';
    const selected = members.filter(m => selectedIds.has(m._id));
    const affected = action === 'add'
      ? selected.filter(m => !m.groups?.some(g => String(g._id) === bulkGroupId))
      : selected.filter(m => m.groups?.some(g => String(g._id) === bulkGroupId));
    const memberIds = affected.map(m => Number(m._id)).filter(Number.isFinite);
    setBulkAction(null);
    setBulkGroupId('');
    if (memberIds.length === 0) {
      // Everyone selected is already in (or already out of) the group.
      addToast(t(action === 'add' ? 'bulk.nothingToAdd' : 'bulk.nothingToRemove'), 'info');
      return;
    }
    const prompt: BulkGroupPrompt = { groupId: bulkGroupId, action, memberIds, preview: null, error: null };
    setBulkPrompt(prompt);
    try {
      const { data } = await api.groups.schedulePreview(bulkGroupId, action, memberIds);
      const empty = data.templates.length === 0 && data.events.length === 0 && data.charges.length === 0;
      if (action === 'add' && empty) {
        await commitBulk(prompt, null);
        finishBulk(action);
        return;
      }
      setBulkPrompt(p => (p ? { ...p, preview: data } : p));
    } catch (err: any) {
      setBulkPrompt(p => (p ? { ...p, error: translateError(err, t) } : p));
    }
  }, [bulkGroupId, selectedIds, bulkAction, members, commitBulk, finishBulk, addToast, t]);

  const handleBulkPromptConfirm = useCallback(async (selection: ScheduleSelection | null) => {
    if (!bulkPrompt) return;
    setBulkPromptConfirming(true);
    try {
      await commitBulk(bulkPrompt, selection);
      finishBulk(bulkPrompt.action);
    } catch (err: any) {
      addToast(
        translateError(err, t, bulkPrompt.action === 'add' ? 'bulk.addToGroupError' : 'bulk.removeFromGroupError'),
        'error'
      );
    } finally {
      setBulkPromptConfirming(false);
    }
  }, [bulkPrompt, commitBulk, finishBulk, addToast, t]);

  const plainGroups = useMemo(() => groups.filter(g => !g.managed), [groups]);

  const memberNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const m of members) names[String(m._id)] = m.name;
    return names;
  }, [members]);

  // Create foreign key options for the filter
  const foreignKeyOptions = useMemo<Record<string, ForeignKeyOption[]>>(() => ({
    users: users.map(u => ({ value: u._id, label: u.name })),
    groups: groups.map(g => ({ value: g._id, label: g.name })),
  }), [users, groups]);

  // Filter members by the active tab. "Unattached" = zero activity enrollments
  // (replaces the legacy external/internal split).
  const displayMembers = useMemo(() => {
    if (memberFilter === 'unattached') {
      return members.filter(m => (m.activityCount ?? 0) === 0);
    }
    return members;
  }, [members, memberFilter]);

  // Count unattached members for the tab badge
  const unattachedCount = useMemo(
    () => members.filter(m => (m.activityCount ?? 0) === 0).length,
    [members],
  );

  const columns: Column<IMember>[] = useMemo(() => {
    const baseColumns: Column<IMember>[] = [
      {
        header: t('members.name'),
        key: 'name',
        render: (member) => {
          const badge = attendanceBadges.get(member._id);
          const isUnattached = (member.activityCount ?? 0) === 0;
          return (
            <div className="members-list-page__name-cell">
              <span>{member.name}</span>
              {isUnattached && (
                <span className="members-list-page__badge members-list-page__badge--external">
                  {t('members.unattached')}
                </span>
              )}
              {badge && badge.total > 0 && (
                <span
                  className={`members-list-page__badge members-list-page__badge--attendance ${
                    badge.rate >= 80
                      ? 'members-list-page__badge--attendance-high'
                      : badge.rate >= 50
                        ? 'members-list-page__badge--attendance-medium'
                        : 'members-list-page__badge--attendance-low'
                  }`}
                >
                  {t('attendanceReport.attendanceBadge', { rate: badge.rate })}
                </span>
              )}
            </div>
          );
        },
      },
      {
        header: t('common.description'),
        key: 'description',
      },
      {
        header: t('members.userCaregiver'),
        sortValue: (member) => member.user ? member.user.name : null,
        render: (member) => member.user ? member.user.name : 'N/A',
      },
      {
        header: t('members.assignGroups'),
        sortValue: (member) => member.groups?.length ?? 0,
        render: (member) => member.groups && member.groups.length > 0
          ? member.groups.map(group => group.name).join(', ')
          : 'None',
      },
    ];

    return baseColumns;
  }, [t, canEdit, attendanceBadges]);

  // Generate export fields from entity definition (only public fields)
  const exportFields = useMemo(() =>
    entityFieldsToExportFields(memberFields, ['name', 'user', 'groups']),
  []);

  // Apply filters to members
  const filteredMembers = useMemo(() => {
    return applyFilters(displayMembers, filters);
  }, [displayMembers, filters]);

  // Compute groups available for "Remove from Group" (groups that at least one selected member belongs to)
  const removeGroupOptions = useMemo(() => {
    if (selectedIds.size === 0) return [];
    const selectedMembers = filteredMembers.filter(m => selectedIds.has(m._id));
    const groupIdSet = new Set<string>();
    selectedMembers.forEach(m => {
      m.groups?.forEach(g => groupIdSet.add(g._id));
    });
    return plainGroups.filter(g => groupIdSet.has(g._id));
  }, [selectedIds, filteredMembers, plainGroups]);

  const handleFilterChange = useCallback((newFilters: FilterValue[]) => {
    setFilters(newFilters);
  }, []);

  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-list-page members-list-page">
      <PageHeader
        title={t('members.title')}
        helpKey="help.admin.members"
        action={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button variant="secondary" style={{ background: 'var(--bg-light)' }} onClick={() => setShowExportModal(true)}>
              {t('export.exportButton')}
            </Button>
            {canCreate && (
              <Link to="/admin/members/create" className="btn btn--primary btn--md">{t('members.createNew')}</Link>
            )}
          </div>
        }
      />

      {/* Member filter tabs — "All" vs "Unattached" (members with zero
          activity enrollments). Replaces the legacy internal/external split. */}
      <div className="members-list-page__tabs">
        <button
          className={`members-list-page__tab ${memberFilter === 'all' ? 'members-list-page__tab--active' : ''}`}
          onClick={() => {
            setMemberFilter('all');
            setShowCleanupPanel(false);
          }}
        >
          {t('members.filterAll')}
        </button>
        <button
          className={`members-list-page__tab ${memberFilter === 'unattached' ? 'members-list-page__tab--active' : ''}`}
          onClick={() => setMemberFilter('unattached')}
        >
          {t('members.filter.unattached')}
          {unattachedCount > 0 && (
            <span className="members-list-page__tab-badge">{unattachedCount}</span>
          )}
        </button>
      </div>

      {/* Cleanup panel toggle (only visible on the Unattached tab) */}
      {memberFilter === 'unattached' && canEdit && (
        <div className="members-list-page__cleanup-toggle">
          <Button
            variant={showCleanupPanel ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setShowCleanupPanel(!showCleanupPanel)}
          >
            {showCleanupPanel ? t('members.hideCleanup') : t('members.showCleanup')}
          </Button>
        </div>
      )}

      {/* Duplicate cleanup panel */}
      {showCleanupPanel && memberFilter === 'unattached' && (
        <div className="members-list-page__cleanup-panel">
          <DuplicateCleanupPanel onCleanupComplete={fetchData} />
        </div>
      )}

      <FilterBar
        fields={memberFields}
        onFilterChange={handleFilterChange}
        foreignKeyOptions={foreignKeyOptions}
      />

      {/* Bulk Action Bar */}
      {canEdit && selectedIds.size > 0 && (
        <div className="members-list-page__bulk-bar">
          <span className="members-list-page__bulk-count">
            {t('bulk.selected', { count: selectedIds.size })}
          </span>
          <Button variant="primary" size="sm" onClick={() => { setBulkAction('addGroup'); setBulkGroupId(''); }}>
            {t('bulk.addToGroup')}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => { setBulkAction('removeGroup'); setBulkGroupId(''); }}>
            {t('bulk.removeFromGroup')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
            {t('bulk.clearSelection')}
          </Button>
        </div>
      )}

      {loading ? (
        <SkeletonTable rows={5} columns={4} />
      ) : filteredMembers.length === 0 ? (
        <EmptyState
          title={memberFilter === 'unattached' ? t('members.emptyUnattachedTitle') : t('members.emptyTitle')}
          description={memberFilter === 'unattached' ? t('members.emptyUnattachedDescription') : t('members.emptyDescription')}
          action={canCreate && memberFilter !== 'unattached' ? { label: t('members.createNew'), href: '/admin/members/create' } : undefined}
        />
      ) : (
        <DataTable
          data={filteredMembers}
          columns={columns}
          editLink={canEdit ? (member) => `/admin/members/${member._id}/edit` : undefined}
          onDelete={canDelete ? handleDeleteClick : undefined}
          onAnonymize={canAnonymize ? handleAnonymizeClick : undefined}
          selectable={canEdit}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t('members.deleteTitle')}
        message={t('members.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />

      <ConfirmModal
        isOpen={!!anonymizeId}
        onClose={() => setAnonymizeId(null)}
        onConfirm={handleAnonymizeConfirm}
        title={t('members.anonymizeTitle')}
        message={t('members.confirmAnonymize')}
        confirmText={t('members.anonymizeConfirmButton')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={anonymizeLoading}
      />

      {/* Bulk Action Modal */}
      <Modal
        isOpen={!!bulkAction}
        onClose={() => { setBulkAction(null); setBulkGroupId(''); }}
        title={bulkAction === 'addGroup'
          ? t('bulk.addToGroupTitle')
          : t('bulk.removeFromGroupTitle')
        }
        size="small"
      >
        <div className="members-list-page__bulk-modal">
          <p style={{ marginBottom: '1rem' }}>
            {t('bulk.selectGroup')}
          </p>
          <SearchableSelect
            options={
              bulkAction === 'removeGroup'
                ? removeGroupOptions.map(g => ({ _id: g._id, name: g.name }))
                : plainGroups.map(g => ({ _id: g._id, name: g.name }))
            }
            value={bulkGroupId || null}
            onChange={(val) => setBulkGroupId(val == null ? '' : String(val))}
            placeholder={t('bulk.selectGroup')}
          />
          <div className="members-list-page__bulk-modal-actions">
            <Button
              variant="secondary"
              onClick={() => { setBulkAction(null); setBulkGroupId(''); }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleBulkAction}
              disabled={!bulkGroupId}
            >
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      </Modal>

      <SchedulePromptModal
        isOpen={!!bulkPrompt}
        onClose={() => { setBulkPrompt(null); fetchData(); }}
        action={bulkPrompt?.action ?? 'add'}
        title={t(bulkPrompt?.action === 'remove' ? 'schedulePrompt.titleRemove' : 'schedulePrompt.titleAdd')}
        memberLabel={bulkPrompt ? t('schedulePrompt.membersLabel', {
          names: bulkPrompt.memberIds.map(mId => memberNames[String(mId)] ?? `#${mId}`).join(', '),
        }) : undefined}
        preview={bulkPrompt?.preview ?? null}
        previewLoading={!!bulkPrompt && !bulkPrompt.preview && !bulkPrompt.error}
        previewError={bulkPrompt?.error ?? null}
        selectable={bulkPrompt?.preview?.selectable ?? false}
        onConfirm={handleBulkPromptConfirm}
        confirming={bulkPromptConfirming}
        memberNames={memberNames}
        paymentsLink="/admin/payments/overview"
      />

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={members}
        filename={`members-${new Date().toISOString().split('T')[0]}`}
        availableFields={exportFields}
        title={t('export.title')}
      />
    </div>
  );
};

export default MembersListPage;
